import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import { useAuthStore, type TokenPair } from '@starling/core/auth/store';
import type { components } from '@/api/types.gen';

type RegisterRequest = components['schemas']['RegisterRequest'];
type LoginRequest = components['schemas']['LoginRequest'];
type InvitationAccept = components['schemas']['InvitationAccept'];
type ContextRequired = components['schemas']['ContextRequired'];
type SelectContextRequest = components['schemas']['SelectContextRequest'];
type ContextsResponse = components['schemas']['ContextsResponse'];
type SwitchContextRequest = components['schemas']['SwitchContextRequest'];
type PasswordResetRequest = components['schemas']['PasswordResetRequest'];
type PasswordResetConfirm = components['schemas']['PasswordResetConfirm'];
type GoogleLoginRequest = components['schemas']['GoogleLoginRequest'];

/** Ответ `POST /auth/login`: либо пара токенов (1 контекст), либо запрос выбора. */
export type LoginResponse = TokenPair | ContextRequired;

/** Дискриминатор union-ответа login: мультиконтекстная форма. */
export function isContextRequired(data: LoginResponse): data is ContextRequired {
  return (data as ContextRequired).context_required === true;
}

export function useLoginMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'login'],
    mutationFn: (input: LoginRequest) =>
      api.post<LoginResponse>('/api/v1/auth/login', input, { skipAuth: true }),
    // Старт новой сессии → сбрасываем React Query кэш прошлой сессии: ключи
    // запросов (напр. ['tenant','settings']) НЕ привязаны к аккаунту, иначе
    // после входа под другим аккаунтом показались бы данные предыдущего.
    // Refresh токена (api/client.ts) идёт мимо этого хука — там кэш не чистим.
    onSuccess: (data) => {
      qc.clear();
      // При мультиконтексте токены не выдаются — экран «Войти как…» обменяет
      // select_token на пару через /auth/select-context. Навигация — в компоненте.
      if (!isContextRequired(data)) setTokens(data);
    },
  });
}

/**
 * Google-вход (GIS id_token, M7.5) — `POST /auth/google`, public.
 *
 * `id_token` — credential из callback официальной GIS-кнопки (см.
 * `GoogleSignInButton`). Контракт ответа идентичен `/auth/login`:
 * `TokenPair` (1 контекст) либо `ContextRequired` (2+ → экран «Войти как…»,
 * навигация — в компоненте). 401 `google_auth_failed` — единый код
 * (невалидный токен / email не приглашён / личность заблокирована),
 * маппинг в сообщение — в компоненте. 404 в норме не случается:
 * кнопка не рендерится без `VITE_GOOGLE_CLIENT_ID`.
 */
export function useGoogleLoginMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'google'],
    mutationFn: (input: GoogleLoginRequest) =>
      api.post<LoginResponse>('/api/v1/auth/google', input, { skipAuth: true }),
    onSuccess: (data) => {
      qc.clear(); // старт новой сессии — сброс кэша прошлой (см. useLoginMutation)
      if (!isContextRequired(data)) setTokens(data);
    },
  });
}

/**
 * Выбор контекста (Войти как…) — `POST /auth/select-context`, public.
 *
 * Обменивает короткоживущий `select_token` (из ответа ContextRequired) и
 * выбранный `membership_id` на обычную пару токенов. 401 — токен протух/битый
 * либо membership не принадлежит личности (маппинг в UI-состояние — на странице).
 */
export function useSelectContextMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'select-context'],
    mutationFn: (input: SelectContextRequest) =>
      api.post<TokenPair>('/api/v1/auth/select-context', input, { skipAuth: true }),
    onSuccess: (pair) => {
      qc.clear(); // старт сессии в выбранной оптике — сброс кэша прошлой
      setTokens(pair);
    },
  });
}

/**
 * Активные оптики (контексты) текущего account — `GET /auth/contexts`, authed.
 *
 * Питает переключатель оптики в сайдбаре: control виден только при 2+
 * контекстах, а в switch-режиме пикера это же query отдаёт список. Данные
 * стабильные в рамках сессии (membership'ы меняются редко) → щедрый staleTime;
 * `qc.clear()` при login/switch/logout всё равно сбросит кэш между сессиями.
 */
export function useContextsQuery() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['auth', 'contexts'],
    queryFn: () => api.get<ContextsResponse>('/api/v1/auth/contexts'),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
}

/**
 * Мид-сессионная смена оптики — `POST /auth/switch-context {membership_id}`, authed.
 *
 * В отличие от select-context НЕ требует select_token: вызывающий уже
 * аутентифицирован, цель обязана принадлежать его account. На успех — как при
 * login: `qc.clear()` + setTokens (иначе залипание данных прошлой оптики, фикс
 * `0ed8fe3`); навигация в кабинет — в компоненте.
 */
export function useSwitchContextMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'switch-context'],
    mutationFn: (input: SwitchContextRequest) =>
      api.post<TokenPair>('/api/v1/auth/switch-context', input),
    onSuccess: (pair) => {
      qc.clear(); // старт сессии в выбранной оптике — сброс кэша прошлой
      setTokens(pair);
    },
  });
}

export function useRegisterMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'register'],
    mutationFn: (input: RegisterRequest) =>
      api.post<TokenPair>('/api/v1/auth/register', input, { skipAuth: true }),
    onSuccess: (pair) => {
      qc.clear();
      setTokens(pair);
    },
  });
}

/**
 * Принятие приглашения сотрудника (`POST /auth/accept-invite`, public).
 *
 * На успех (201) бэк отдаёт `TokenPair` — сразу сохраняем в auth-store,
 * чтобы фронт мог редиректнуть пользователя в админку без отдельного логина.
 *
 * Семантические ошибки контракта (см. openapi-m1.yaml D5):
 *   * 404 `invitation_not_found`
 *   * 410 `invitation_expired`
 *   * 409 `invitation_already_used`
 *
 * Маппинг этих кодов в локализованные сообщения — задача страницы
 * `AcceptInvitePage`, хук занят только транспортом + setTokens.
 */
export function useAcceptInviteMutation() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'accept-invite'],
    mutationFn: (input: InvitationAccept) =>
      api.post<TokenPair>('/api/v1/auth/accept-invite', input, { skipAuth: true }),
    onSuccess: (pair) => {
      qc.clear();
      setTokens(pair);
    },
  });
}

/**
 * Запрос ссылки сброса пароля (`POST /auth/password-reset/request`, public).
 *
 * Контракт (M7.3): **ВСЕГДА 200** `{"status":"ok"}`, независимо от того,
 * зарегистрирован ли email (анти-enumeration). Поэтому у мутации нет
 * «бизнес-ошибки»: любой не-200 — это сеть/инфраструктура, страница показывает
 * generic-alert, но раскрытия существования аккаунта не происходит.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'request'],
    mutationFn: (input: PasswordResetRequest) =>
      api.post<{ status: string }>('/api/v1/auth/password-reset/request', input, {
        skipAuth: true,
      }),
  });
}

/**
 * Установка нового пароля по reset-токену (`POST /auth/password-reset/confirm`, public).
 *
 * Успех — 204 (без авто-логина: пользователь входит сам через /auth/login).
 * Семантические ошибки контракта (маппинг в UI-состояние — на странице):
 *   * 400 `reset_token_invalid` — токен не найден / протух / уже использован.
 *   * 422 — валидация пароля (min 8, pattern токена).
 */
export function useConfirmPasswordReset() {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'confirm'],
    mutationFn: (input: PasswordResetConfirm) =>
      api.post<void>('/api/v1/auth/password-reset/confirm', input, { skipAuth: true }),
  });
}

export function useLogout(): () => void {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  // Чистим кэш и при выходе — чтобы данные аккаунта не оставались в памяти
  // до следующего входа (гигиена + подстраховка к сбросу на входе).
  return () => {
    qc.clear();
    logout();
  };
}
